import {FollowingScopeContent} from "@/src/features/following/feed-scope";
import FollowingFeed from "@/src/features/following/feed";
import {useFollowingScope} from "@/src/features/following/hooks";
import { useFeedDemand } from "@/src/hooks/common/use-feed-demand";
import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import FeaturedNewsCarousel from "@/src/components/news/featured-news/featured-news-carousel";
import { PostSkeleton } from "@/src/components/posts/loading-skeletons/post-skeleton";
import PostCard from "@/src/components/posts/post-card";
import CreatePostModal from "@/src/components/posts/post-creation-modal";
import PostDetailModal from "@/src/components/posts/post-detail-modal";
import ReactorModal from "@/src/components/posts/post-reactor-modal";
import { RightPanel } from "@/src/components/posts/right-panel/right-panel";
import FeedSortControl from "@/src/components/posts/feed-sort-control";
import type { FeedMode, FeedSort } from "@/src/utils/feed-sort";
import InstallAppBanner from "@/src/components/pwa/install-app-banner";
import ScButton from "@/src/components/ui-kit/button";
import PermissionDenied from "@/src/components/ui-kit/permission-denied";
import { usePermissions } from "@/src/hooks/use-permissions";
import ScSearchInput from "@/src/components/ui-kit/search-bar";
import { usePosts } from "@/src/hooks/features/posts/use-posts";
import { useScrollRestoration } from "@/src/hooks/common/use-scroll-restoration";
import NewPostsPill from "@/src/components/posts/new-posts-pill";
import { FeedReactionsProvider } from "@/src/hooks/features/posts/use-feed-reactions";
import { useResponsiveGap } from "@/src/hooks/use-responsive-gap";
import { useTheme } from "@/src/styles/theme-provider";
import { Post } from "@/src/models/post.model";
import { useRouter, useLocalSearchParams } from "expo-router";
import { AddCircle } from "iconsax-react-nativejs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FlatList,
  Image,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { ActivityIndicator } from "react-native-paper";

type FeedModeProps = Readonly<{
  initialSort: FeedSort;
  showFollowing: boolean;
  onModeChange: (mode: FeedMode) => void;
}>;

const AllFeeds = ({ initialSort, showFollowing, onModeChange }: FeedModeProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { varStyle } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const gap = useResponsiveGap();

  const {
    displayedPosts: posts,
    isLoading,
    isError,
    refetch,
    loadMore,
    prefetchMore,
    hasNextPage,
    hasMoreSearchResults,
    isFetchingNextPage,
    search,
    setSearch,
    handleSearch,
    createPost,
    modalOpen,
    setModalOpen,
    editPost,
    setEditPost,
    viewerOpen,
    viewerPostId,
    handleCloseViewer,
    confirmDeleteOpen,
    handleDeleteConfirm,
    setConfirmDeleteOpen,
    setDeletePostId,
    handleSubmit,
    isSearchLoading,
    hasSearched,
    showReactorModal,
    setReactorModalVisibility,
    handleCloseReactorModal,
    sort,
    setSort,
    newPostsCount,
    showNewPosts,
    isShowingNewPosts,
    setFeedScrollY,
    hasFeedData,
  } = usePosts(false, undefined, { stableOrder: true });
  useEffect(() => {
    if (sort !== initialSort) setSort(initialSort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSort]);

  const scroll = useScrollRestoration(`feeds:${sort}`);
  const demand = useFeedDemand({ scope: `${sort}:${search}`, busy: isLoading || isFetchingNextPage || isSearchLoading || isError,
    hasMore: hasSearched ? hasMoreSearchResults : !!hasNextPage, loadMore, prefetchMore });

  const { canCreate, canView } = usePermissions();
  const canCreatePost = canCreate("post");
  const canViewPost = canView("post");

  /**
   * Pagination is driven from this ScrollView, not from the FlatList inside it.
   *
   * A VirtualizedList nested in a ScrollView has unbounded height, so it renders
   * every row and considers itself permanently scrolled to the end — its
   * `onEndReached` fired on mount and again on every render, which is what was
   * requesting page after page without anybody scrolling. The real scroll
   * position lives out here, so the next page is asked for only once the reader
   * has actually reached the bottom of the posts already loaded.
   */
  const handleFeedScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset } = event.nativeEvent;
    scroll.onScrollY(contentOffset.y);
    setFeedScrollY(contentOffset.y);
    demand.onScroll(event);
  };

  // The posts on screen right now — one page, then two as the reader scrolls.
  const postIds = posts.map((post) => post.ItemId);

  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [currentPost, setCurrentPost] = useState<Post>();

  // Early return if user cannot view posts
  if (!canViewPost) {
    return (
      <View style={varStyle} className={`flex-1 bg-obsidian ${gap}`}>
        <PermissionDenied />
      </View>
    );
  }

  const renderPostCard = (post: Post) => (
    <PostCard
      key={post.ItemId}
      post={post}
      onReactorCount={() => {
        setReactorModalVisibility(true);
        setCurrentPost(post);
      }}
      onEdit={(post) => {
        setEditPost(post);
        setModalOpen(true);
      }}
      onDelete={(post) => {
        setDeletePostId(post.ItemId);
        setConfirmDeleteOpen(true);
      }}
    />
  );

  const listHeaderComponent =
    hasSearched && posts.length > 0 ? (
      <View className="flex-row items-center gap-1 px-1 flex-wrap">
        <Text className="text-sm text-gray-500">
          {t("LABEL_SEARCH_RESULTS_FOR")}
        </Text>
        <Text className="text-sm font-semibold text-gray-800">{search}</Text>
      </View>
    ) : null;

  const listFooterComponent = isFetchingNextPage ? (
    <ActivityIndicator size="small" className="py-4" />
  ) : (hasSearched ? hasMoreSearchResults : hasNextPage) ? (
    <ScButton text="LABEL_LOAD_MORE" mode="outlined" onPress={demand.manualLoadMore} />
  ) : null;

  const listEmptyComponent = isSearchLoading ? (
    <View className="items-center py-10">
      <ActivityIndicator size="large" />
    </View>
  ) : hasSearched ? (
    <View className="items-center py-10 gap-3">
      <Image
        source={require("@/src/assets/images/empty.png")}
        style={{ width: 200, height: 200 }}
        resizeMode="contain"
      />
      <Text className="text-gray-800 text-sm font-medium">
        {t("LABEL_NO_RESULTS_FOR")}
      </Text>
      <Text className="text-gray-500 text-sm font-semibold">{search}</Text>
      <Text className="text-gray-400 text-xs text-center px-8">
        {t("LABEL_TRY_DIFFERENT_KEYWORDS")}
      </Text>
    </View>
  ) : (
    <View className="items-center py-6">
      <Text className="text-gray-dark text-sm mb-4">
        {t("LABEL_EMPTY_POST")}
      </Text>
      <Image
        source={require("@/src/assets/images/empty.png")}
        style={{ width: 300, height: 300 }}
        resizeMode="contain"
      />
    </View>
  );

  return (
    <View
      style={varStyle}
      className={`flex-1 flex-row w-full bg-obsidian ${gap}`}
    >
      <View className="w-[100%] md:w-[68%]">
      {newPostsCount > 0 && !hasSearched && (
        <NewPostsPill
          count={newPostsCount}
          loading={isShowingNewPosts}
          onPress={async () => {
            await showNewPosts();
            scroll.scrollTo(0);
            setFeedScrollY(0);
          }}
        />
      )}
      <ScrollView
        ref={scroll.ref}
        className="flex-1"
        showsVerticalScrollIndicator={false}
        onScroll={handleFeedScroll}
        onContentSizeChange={(w, h) => { scroll.onContentSizeChange(w, h); demand.onContentSizeChange(w, h); }}
        onLayout={event => { scroll.onLayout(event); demand.onLayout(event); }}
        scrollEventThrottle={16}
        contentContainerStyle={{
          flexGrow: 1,
          paddingBottom: isMobile ? 24 : 0,
        }}
      >
        <View className={isMobile ? "gap-3" : "gap-4"}>
          {/* Search + Create Post */}
          {isMobile ? (
            <ScSearchInput
              value={search}
              onChangeText={setSearch}
              handleSearch={handleSearch}
              placeholderKey="LABEL_SEARCH_FEEDS"
              surface="screen"
              containerClassName="rounded-2xl"
              onCreatePress={
                canCreatePost ? () => setModalOpen(true) : undefined
              }
            />
          ) : (
            <View className="flex-row rounded-2xl border-ash-3 justify-between border bg-chalk p-4">
              <ScSearchInput
                containerClassName="rounded-2xl w-[50%] bg-obsidian"
                placeholderKey="LABEL_SEARCH_FEEDS"
                value={search}
                handleSearch={handleSearch}
                onChangeText={setSearch}
              />
              {canCreatePost && (
                <ScButton
                  text="BTN_CREATE"
                  onPress={() => setModalOpen(true)}
                  icon={<AddCircle color="white" />}
                />
              )}
            </View>
          )}

          <InstallAppBanner />

          <FeaturedNewsCarousel />

          {/* Sorting applies to the feed, not to results. Search returns its own
              ordering (newest first) and re-running it on a sort change is not
              wired, so showing Hottest/Latest over results would be a control
              that does nothing. */}
          {!hasSearched && (
            <FeedSortControl
              sort={sort}
              showFollowing={showFollowing}
              onSortChange={(mode) =>
                mode === "following" ? onModeChange(mode) : (setSort(mode), onModeChange(mode))
              }
            />
          )}

          {/* Posts Section */}
          <View className="gap-4">
            {isLoading && [1, 2, 3].map((item) => <PostSkeleton key={item} />)}

            {isError && !hasFeedData && (
              <View className="items-center py-6 gap-2">
                <Image
                  source={require("@/src/assets/images/error.png")}
                  style={{ resizeMode: "contain", width: 300, height: 300 }}
                  className="w-16 h-16 rounded-full"
                  alt="error"
                />
                <Text className="text-gray-dark text-sm">
                  {t("ERROR_LOADING_POST")}
                </Text>
                <ScButton
                  mode="outlined"
                  text="BTN_RETRY"
                  onPress={() => refetch()}
                />
              </View>
            )}

            {!isLoading && (!isError || hasFeedData) && (
              <FeedReactionsProvider postIds={postIds}>
                {/*
                  Web: posts render as plain views, with no scroller of their own.
                  A nested FlatList with scrollEnabled={false} makes
                  react-native-web's ScrollViewBase apply
                  `scrollDisabled: { overflow: hidden, touchAction: "none" }`, and
                  `touch-action: none` suppresses native touch panning across the
                  whole posts area -- on a phone the feed then only scrolls when
                  the drag starts above the list. Keeping a single scroller (the
                  outer ScrollView, which owns pagination per handleFeedScroll) is
                  what the comment below has always assumed. Native keeps the
                  FlatList, so virtualisation there is unchanged.
                */}
                {Platform.OS === "web" ? (
                  <View className="gap-4">
                    {listHeaderComponent}
                    {posts.length > 0
                      ? posts.map(renderPostCard)
                      : listEmptyComponent}
                    {listFooterComponent}
                  </View>
                ) : (
                  <FlatList
                    data={posts}
                    keyExtractor={(item) => item.ItemId}
                    contentContainerStyle={{ gap: 16 }}
                    ListHeaderComponent={listHeaderComponent}
                    renderItem={({ item: post }) => renderPostCard(post)}
                    // The parent ScrollView owns the gesture and the pagination
                    // trigger; see handleFeedScroll.
                    scrollEnabled={false}
                    ListFooterComponent={listFooterComponent}
                    ListEmptyComponent={listEmptyComponent}
                  />
                )}
              </FeedReactionsProvider>
            )}
          </View>
        </View>
      </ScrollView>
      </View>

      {/* Right Panel */}
      <RightPanel
        selectedTag={selectedTag}
        onTagSelect={setSelectedTag}
        onLeaderboardSeeAll={() => router.push("/leaderboard")}
      />

      {/* Modals */}
      <CreatePostModal
        visible={modalOpen}
        post={editPost ?? undefined}
        onClose={() => {
          setModalOpen(false);
          setEditPost(null);
        }}
        onSubmit={handleSubmit}
        isPosting={createPost.isPending}
      />

      <PostDetailModal
        visible={viewerOpen}
        postId={viewerPostId as string}
        onClose={handleCloseViewer}
      />

      <ConfirmModal
        visible={confirmDeleteOpen}
        title="QUEST_CONFIRM_DELETE_POST"
        description="LABEL_THIS_ACTION_CANNOT_BE_UNDONE"
        confirmText="DELETE"
        cancelText="CANCEL"
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      <ReactorModal
        showModal={showReactorModal}
        onClose={handleCloseReactorModal}
        reactions={currentPost?.reactionCount ?? {}}
        postId={currentPost?.ItemId}
      />
    </View>
  );
};

const parseMode = (scope?: string, sort?: string): FeedMode =>
  scope === "following" ? "following" : sort === "latest" ? "latest" : "hottest";

/**
 * One selector: Hottest | Latest | Following. Hottest and Latest are the
 * main feed's two orders; Following is the Following feed (its own default
 * order). Following is only offered when the server enrolment check passes.
 */
function Feeds() {
  const { available: followingEnabled } = useFollowingScope();
  const params = useLocalSearchParams<{ scope?: string; sort?: string }>();
  const router = useRouter();
  const [mode, setMode] = useState<FeedMode>(parseMode(params.scope, params.sort));
  const [lastSort, setLastSort] = useState<FeedSort>(mode === "latest" ? "latest" : "hottest");
  useEffect(() => {
    const next = parseMode(params.scope, params.sort);
    setMode(next);
    if (next !== "following") setLastSort(next);
  }, [params.scope, params.sort]);
  const change = (next: FeedMode) => {
    setMode(next);
    if (next !== "following") setLastSort(next);
    router.setParams(next === "following" ? { scope: "following", sort: undefined } : { scope: undefined, sort: next });
  };
  const all = <AllFeeds initialSort={lastSort} showFollowing={followingEnabled} onModeChange={change} />;
  return (
    <View className="flex-1 gap-4">
      <FollowingScopeContent
        scope={mode === "following" ? "following" : "all"}
        available={followingEnabled}
        following={<FollowingFeed onModeChange={change} />}
        all={all}
        onShowAll={() => change(lastSort)}
      />
    </View>
  );
}
export default Feeds;
